import { useCloseModal, useConfigureModalController, useModalController } from '@/atoms'
import { selectTerritoryLicensingLimits } from '@/features/domain/territory'
import { selectUserConfiguration } from '@/features/domain/user'
import { addDays, parse } from 'date-fns/esm'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useControllerData } from './useControllerData'

type ModalState = 'ready' | 'submitting'

export interface InputError {
  hasError: boolean
  message: string
}

export interface ModalData {
  percentage: number
  inputError: InputError
  storageError: InputError
  newPlanningRange: number
  isReduced: boolean
  ordersCanBeExported: boolean
  exportStartDate?: Date
  exportEndDate?: Date
  format: 'CSV' | 'XLSX' | 'PDF'
  model: 'export' | 'delete' | 'planningRange'
  canDeleteOrders?: boolean
}

export const modalId = 'planningRange'

export const useController = () => useModalController<ModalData, ModalState>(modalId)

export const useConfigureController = () => {
  const { planningRange } = useSelector(selectTerritoryLicensingLimits)
  const { today } = useSelector(selectUserConfiguration)
  const data = useControllerData()
  const close = useCloseModal()

  // INITIALIZATION
  const [options] = useState(() => ({
    createInitialState: () => ({
      close,
      data: {
        status: 'ready' as ModalState,
        inputError: { hasError: false, message: '' },
        storageError: { hasError: false, message: '' },
        newPlanningRange: data.planningRange,
        percentage: data.percentage,
        isReduced: false,
        ordersCanBeExported: false,
        format: 'CSV' as const,
        model: 'planningRange' as const,
        canDeleteOrders: false,
      },
    }),
  }))

  const ctrl = useConfigureModalController<ModalData, ModalState>(modalId, options)
  const {
    updateData,
    data: { newPlanningRange },
  } = ctrl

  useEffect(() => {
    const todayDate = parse(today, 'yyyyMMdd', new Date())
    const exportStartDate = addDays(todayDate, newPlanningRange + 1) // +1 to start the day after the new planning range
    const exportEndDate = addDays(todayDate, planningRange)

    updateData({
      exportStartDate,
      exportEndDate,
    })
  }, [updateData, today, planningRange, newPlanningRange])

  return ctrl
}
